import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, XCircle, Lock, Target, Users, RefreshCcw, FileText, Shield, Award, Brain } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6, ease, delay } });
const stagger = (i) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.55, ease, delay: i * 0.1 } });

const DIFF = [
  { icon: Lock, title: "Official Portals Only", short: "No shortcuts. No spam tools.", desc: "Every application is submitted through a company's official career portal. We don't use automation scrapers, mass-apply shortcuts, or any tool that bypasses the standard application process." },
  { icon: Target, title: "Strategic, Not Random", short: "Every application has a reason.", desc: "We don't apply everywhere. Before each cycle, our team reviews open roles against your profile and approved direction. If a role isn't a genuine fit, we skip it." },
  { icon: Users, title: "Limited Slots", short: "Intentional capacity control.", desc: "We deliberately cap the number of active clients. This is a quality decision — every candidate deserves focused, serious attention. We don't want to become a factory." },
  { icon: RefreshCcw, title: "Weekly Feedback Loop", short: "Built-in iteration, every week.", desc: "At the end of each week, we share what was submitted, any signals, and our market observations. You give feedback, and we incorporate it immediately." },
  { icon: FileText, title: "Full Profile Optimization", short: "Included, not an upsell.", desc: "Resume, LinkedIn, and cover letter support are included in every plan. We set you up properly before the first application leaves our queue." },
  { icon: Shield, title: "Honest Communication", short: "No false promises. Ever.", desc: "If the market is slow, we'll say so. If your resume needs work, we'll say that too. Honest communication is the foundation of a relationship where we can actually help." },
  { icon: Award, title: "Strategy Refund Guarantee", short: "Satisfaction-first policy.", desc: "Not happy with the strategy we're using? Request a full refund. Guaranteed based on strategy quality — not hiring outcomes or interview results." },
  { icon: Brain, title: "Deep Tech Focus", short: "We know the market we serve.", desc: "We work exclusively in tech. We understand the difference between ML Ops and MLflow pipelines, between application security and cloud security." },
];

const WhyUsPage = () => (
  <div data-testid="why-us-page" className="bg-white">
    <SEOHead title="Why ApplyStrat | What Sets Us Apart" description="ApplyStrat uses official portals only, applies strategically (not randomly), has limited slots, a weekly feedback loop, full profile optimization, and a strategy refund guarantee." canonical="https://applystrat.com/why-us" />

    <section className="pt-36 pb-16 bg-white text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.12) 0%, transparent 65%)" }}>
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()}>
          <p className="section-label-orange mb-4">Differentiators</p>
          <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-6">Why ApplyStrat</h1>
          <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">Not a staffing agency. Not a resume service. A focused execution partner for serious tech candidates.</p>
        </motion.div>
      </div>
    </section>

    <section className="py-16 pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {DIFF.map((d, i) => (
            <motion.div key={d.title} {...stagger(i)} data-testid={`differentiator-${i}`} className="bg-surface-raised rounded-3xl p-8 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400 group">
              <div className="flex items-start gap-5">
                <motion.div
                  whileHover={{ scale: 1.2, rotate: -8 }}
                  transition={{ type: "spring", stiffness: 380, damping: 14 }}
                  className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:bg-brand-light transition-colors"
                >
                  <d.icon size={20} className="text-ink-secondary group-hover:text-brand transition-colors" />
                </motion.div>
                <div>
                  <p className="text-brand text-xs font-medium uppercase tracking-widest mb-1">{d.short}</p>
                  <h2 className="text-ink font-medium text-lg mb-2">{d.title}</h2>
                  <p className="text-ink-secondary text-sm leading-relaxed">{d.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-surface-raised border-t border-black/5">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()} className="text-center mb-12">
          <h2 className="text-3xl font-medium text-ink">What We Are vs. What We're Not</h2>
        </motion.div>
        <div className="grid grid-cols-2 gap-5">
          {[
            { label: "We are", items: ["A focused execution partner", "Strategy-first, quality-focused", "Tech-only service", "Honest communicators", "Selective by design"], positive: true },
            { label: "We are not", items: ["A staffing agency", "A mass-apply service", "A generalist recruiter", "A guaranteed placement firm", "Available to everyone"], positive: false },
          ].map((col) => (
            <motion.div key={col.label} {...fadeUp(col.positive ? 0.1 : 0.2)} className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
              <p className={`font-medium text-sm mb-4 ${col.positive ? "text-green-600" : "text-red-400"}`}>{col.label}</p>
              <ul className="space-y-3">
                {col.items.map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    {col.positive
                      ? <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                      : <XCircle size={12} className="text-red-400 flex-shrink-0" />
                    }
                    <span className="text-ink-secondary text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-white border-t border-black/5">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl font-medium text-ink mb-5">Ready to Work With a Partner That Executes?</h2>
          <p className="text-ink-secondary text-base mb-8">Apply now and we'll review your profile within 24 hours.</p>
          <Button asChild size="lg" className="shadow-sm hover:scale-[1.02]">
            <Link to="/apply" data-testid="why-us-apply-button">Apply to Work With Us <ArrowRight size={18} /></Link>
          </Button>

        </motion.div>
      </div>
    </section>
  </div>
);

export default WhyUsPage;
